/**
 * patientPortalAdmin.service.ts — Admin > Account > Patient Portal (legacy
 * admin_portal_list.php): the portal's settings and the links on its
 * dashboard, through /patient-portal-settings and /patient-portal-links.
 * Writes need Admin Access.
 */
import { apiClient } from '@/services/apiClient';
import type { ApiResponse } from '@/types/api.types';

export interface PortalSettings {
  enabled: boolean;
  inviteSubject: string;
  /** HTML. */
  inviteMessage: string;
  footerContent: string;
  welcDisclaimerActive: boolean;
  welcDisclaimer: string;
  dobRequired: boolean;
  cellphoneRequired: boolean;
}

export interface PortalLink {
  id: number;
  title: string;
  url: string;
  content: string;
  /** '' none, a portal form (demographics_form …) or a Formstack form id. */
  form: string;
  newWindow: boolean;
  order: number;
  active: boolean;
}

export type PortalLinkPayload = Pick<PortalLink, 'title' | 'url' | 'content' | 'form' | 'newWindow'>;

export interface PortalAdmin {
  /** False when the practice has no portal row (nothing can be saved). */
  configured: boolean;
  settings: PortalSettings | null;
  links: PortalLink[];
  formChoices: { value: string; label: string }[];
}

export const PORTAL_LINK_TITLE_MAX = 255;
export const INVITE_SUBJECT_MAX = 255;

const flag = (v: unknown) => v === true || v === 1 || v === '1';

function toSettings(raw: Record<string, unknown>): PortalSettings {
  return {
    enabled: flag(raw.enabled),
    inviteSubject: String(raw.inviteSubject ?? ''),
    inviteMessage: String(raw.inviteMessage ?? ''),
    footerContent: String(raw.footerContent ?? ''),
    welcDisclaimerActive: flag(raw.welcDisclaimerActive),
    welcDisclaimer: String(raw.welcDisclaimer ?? ''),
    dobRequired: flag(raw.dobRequired),
    cellphoneRequired: flag(raw.cellphoneRequired),
  };
}

function toLink(raw: Record<string, unknown>): PortalLink {
  return {
    id: Number(raw.customPageID) || 0,
    title: String(raw.customTitle ?? ''),
    url: String(raw.customUrl ?? ''),
    content: String(raw.customContent ?? ''),
    form: String(raw.standardLink ?? ''),
    newWindow: flag(raw.openInBlank),
    order: Number(raw.orderID) || 0,
    active: flag(raw.active),
  };
}

export function getPortalAdmin(): Promise<PortalAdmin> {
  return apiClient.get<ApiResponse<Record<string, unknown>>>('/patient-portal-settings').then((r) => {
    const d = r.data.data ?? {};
    return {
      configured: flag(d.configured),
      settings: d.settings ? toSettings(d.settings as Record<string, unknown>) : null,
      links: (Array.isArray(d.links) ? (d.links as Record<string, unknown>[]) : []).map(toLink),
      formChoices: (Array.isArray(d.formChoices) ? (d.formChoices as Record<string, unknown>[]) : []).map((c) => ({
        value: String(c.value ?? ''),
        label: String(c.label ?? ''),
      })),
    };
  });
}

export function savePortalSettings(s: PortalSettings): Promise<void> {
  return apiClient.put('/patient-portal-settings', { ...s, inviteSubject: s.inviteSubject.trim() }).then(() => undefined);
}

function linkBody(l: PortalLinkPayload) {
  return { customTitle: l.title.trim(), customUrl: l.url.trim(), customContent: l.content, standardLink: l.form, openInBlank: l.newWindow };
}

export function createPortalLink(l: PortalLinkPayload): Promise<void> {
  return apiClient.post('/patient-portal-links', linkBody(l)).then(() => undefined);
}

export function updatePortalLink(id: number, l: PortalLinkPayload): Promise<void> {
  return apiClient.put(`/patient-portal-links/${id}`, linkBody(l)).then(() => undefined);
}

export function deletePortalLink(id: number): Promise<void> {
  return apiClient.delete(`/patient-portal-links/${id}`).then(() => undefined);
}

export function restorePortalLink(id: number): Promise<void> {
  return apiClient.post(`/patient-portal-links/${id}/restore`).then(() => undefined);
}

/** The live links, in their new order. */
export function reorderPortalLinks(ids: number[]): Promise<void> {
  return apiClient.post('/patient-portal-links/reorder', { ids }).then(() => undefined);
}
